import React, { useRef, useState, useEffect, useMemo } from 'react';
import { ResourceManager } from './components/ResourceManager';
import ImageUploadGallery from '@/components/ImageUploadGallery';
import VideoUploadGallery from '@/components/VideoUploadGallery';
import { formatUGX } from '@/utils/currency';
import { useSupabaseResource } from '@/hooks/useSupabaseResource';
import { supabase } from '@/lib/supabaseClient';
import { Download, Upload } from 'lucide-react';

const statusOptions = [
  { label: 'Available', value: 'available' },
  { label: 'Sold', value: 'sold' },
  { label: 'Reserved', value: 'reserved' },
];

const buildSelect = (includeLand, includeShortDescription) => {
  const base = [
    'id',
    'title',
    'location',
    'google_location',
    'bedrooms',
    'price',
    'status',
    'category',
    'description',
    'images',
    'featured_image',
    'is_featured',
    'video_url',
  ];
  if (includeShortDescription) base.push('short_description');
  if (includeLand) base.splice(4, 0, 'land_size', 'land_size_unit'); // keep near price
  base.push('selected_agent_id'); // Include selected agent ID
  return base.join(',');
};

const PropertiesModule = () => {
  const [supportsLandFields, setSupportsLandFields] = useState(false);
  const [supportsShortDescription, setSupportsShortDescription] = useState(true);
  const [selectFields, setSelectFields] = useState(buildSelect(false, true));
  const { records, refresh } = useSupabaseResource('properties', { select: selectFields });
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [agents, setAgents] = useState([]);
  const fileInputRef = useRef();

  const normalizeCategory = (value) => (value || '').toLowerCase().trim();
  const findCategory = (value) =>
    categories.find((cat) => normalizeCategory(cat.name) === normalizeCategory(value));
  const findAgent = (id) => agents.find((agent) => agent.id === id);

  // Function to parse and evaluate mathematical expressions for land size
  const parseLandSize = (input) => {
    if (!input || typeof input !== 'string') return null;
    
    // Clean the input - convert common terms to operators
    let cleaned = input.trim().toLowerCase()
      .replace(/by/g, '*')
      .replace(/times/g, '*')
      .replace(/÷/g, '/')
      .replace(/\s+/g, ''); // Remove spaces
    
    // Only allow numbers, basic operators, and decimal points
    if (!/^[\d+\-*/().\s]+$/.test(cleaned)) {
      return null; // Invalid characters
    }
    
    try {
      // Basic safety check - limit to simple expressions
      if (cleaned.split('').filter(char => '+-*/'.includes(char)).length > 2) {
        return null; // Too many operators for safety
      }
      
      // Evaluate the expression safely
      const result = Function(`"use strict"; return (${cleaned})`)();
      
      if (typeof result === 'number' && isFinite(result) && result > 0) {
        return Math.round(result * 100) / 100; // Round to 2 decimal places
      }
    } catch (error) {
      console.warn('Invalid land size expression:', input);
    }
    
    return null;
  };

  useEffect(() => {
    fetchCategories();
    fetchAgents();
  }, []);

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('property_categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
    if (!error) {
      setCategories(data || []);
    }
  };

  const fetchAgents = async () => {
    const { data, error } = await supabase
      .from('listing_team')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
    if (!error) {
      setAgents(data || []);
    }
  };

  useEffect(() => {
    const checkColumns = async () => {
      const { error } = await supabase
        .from('properties')
        .select('land_size, land_size_unit', { head: true, count: 'exact' })
        .limit(1);

      const hasLandColumns = !error;
      if (error) {
        console.warn('Land size column check warning:', error.message);
      }
      setSupportsLandFields(hasLandColumns);
      setSelectFields(buildSelect(hasLandColumns, supportsShortDescription));
    };
    checkColumns();
  }, [supportsShortDescription]);

  const exportToCSV = () => {
    const baseHeaders = [
      'title',
      'location',
      'google_location',
      'bedrooms',
      'price',
      'status',
      'category',
      'description',
      'is_featured',
      'video_url',
    ];
    if (supportsShortDescription) baseHeaders.splice(6, 0, 'short_description');
    const headers = supportsLandFields
      ? ['title', 'location', 'google_location', 'bedrooms', 'land_size', 'land_size_unit', ...baseHeaders.slice(4)]
      : baseHeaders;
    const csvContent = [
      headers.join(','),
      ...records.map(record => headers.map(h => {
        if (h === 'category') {
          const category = findCategory(record[h]);
          return `"${category ? category.name : record[h] || ''}"`;
        }
        return `"${record[h] || ''}"`;
      }).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'properties.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        alert('CSV must have headers and at least one row');
        return;
      }
      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const requiredHeaders = ['title', 'location', 'price', 'status', 'category'];
      const missing = requiredHeaders.filter(h => !headers.includes(h));
      if (missing.length) {
        alert(`Missing required headers: ${missing.join(', ')}`);
        return;
      }
      const properties = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.replace(/"/g, '').trim());
        const obj = {};
        headers.forEach((h, i) => {
          obj[h] = values[i] || '';
        });
        // map category name to known category label
        const category = categories.find(cat => cat.name.toLowerCase() === obj.category.toLowerCase());
        obj.category = category ? category.name.toLowerCase() : obj.category.toLowerCase();
        // transform
        const isLand = category && category.name.toLowerCase() === 'land';
        obj.price = Number(obj.price) || 0;
        obj.bedrooms = isLand ? null : (obj.bedrooms ? Number(obj.bedrooms) : null);
        if (supportsLandFields) {
          obj.land_size = isLand ? parseLandSize(obj.land_size) : null;
          obj.land_size_unit = isLand ? obj.land_size_unit : null;
        } else {
          delete obj.land_size;
          delete obj.land_size_unit;
        }
        obj.is_featured = obj.is_featured === 'true';
        return obj;
      });
      const { error } = await supabase.from('properties').insert(properties);
      if (error) throw error;
      alert('Bulk upload successful');
      refresh();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (record) => {
    // Ensure images is an array for existing records
    const images = record.images
      ? (Array.isArray(record.images) ? record.images : [record.images])
      : (record.featured_image ? [record.featured_image] : []);
    const matchedCategory = findCategory(record.category);

    // Note: Agent matching based on contact fields is no longer supported
    // as contact fields were removed from the properties table
    // The agent selection is now managed through the selected_agent_id field

    return {
      ...record,
      category: matchedCategory ? matchedCategory.name : record.category,
      images,
      video_url: record.video_url || '',
      google_location: record.google_location || '',
      selected_agent_id: record.selected_agent_id || '', // Use existing selected agent or empty string
    };
  };

  const getFormFields = (formState = {}) => {
    const selectedCategory = findCategory(formState.category);
    const isLand = selectedCategory && normalizeCategory(selectedCategory.name) === 'land';
    const isMapCategory = selectedCategory
      ? ['house', 'houses', 'home', 'homes', 'land', 'plot', 'plots'].includes(normalizeCategory(selectedCategory.name))
      : false;
    
    const fields = [
      { name: 'title', label: 'Title', placeholder: 'Ultra Modern Apartment' },
      { name: 'location', label: 'Location', placeholder: 'Kira, Kampala' },
      {
        name: 'bedrooms',
        label: 'Bedrooms',
        type: 'select',
        options: [
          { label: '1 Bedroom', value: '1' },
          { label: '2 Bedrooms', value: '2' },
          { label: '3 Bedrooms', value: '3' },
        ],
        disabled: isLand,
      },
      { name: 'price', label: 'Price', type: 'number', placeholder: '55000000' },
      { name: 'status', label: 'Status', type: 'select', options: statusOptions },
      { name: 'category', label: 'Category', type: 'select', options: categories.map(cat => ({
        label: cat.name,
        value: cat.name
      }))},
      { 
        name: 'selected_agent_id', 
        label: 'Agent', 
        type: 'select', 
        options: [
          { label: 'Select an agent', value: '' },
          ...agents.map(agent => ({
            label: agent.name,
            value: agent.id
          }))
        ],
        required: false,
        placeholder: 'Choose an agent for this property'
      },
      { name: 'images', label: 'Property Images', type: 'custom', component: ImageUploadGallery },
      { name: 'video_url', label: 'Property Video URL', placeholder: 'https://www.youtube.com/watch?v=...', type: 'url' },
        ...(supportsShortDescription
          ? [{ name: 'short_description', label: 'Short Description', placeholder: 'Brief summary for listings' }]
          : []),
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'is_featured', label: 'Mark as Featured', type: 'checkbox' },
    ];
    if (isMapCategory) {
      fields.splice(2, 0, { name: 'google_location', label: 'Google Maps Link', placeholder: 'https://maps.google.com/?q=...' });
    }
    if (supportsLandFields) {
      fields.splice(3, 0,
        {
          name: 'land_size',
          label: 'Land Size',
          type: 'text',
          placeholder: '50 or 50*100 or 100/2 or 50 by 75',
          disabled: !isLand,
          required: isLand,
        },
        {
          name: 'land_size_unit',
          label: 'Size Unit',
          type: 'select',
          options: [
            { label: 'Acres', value: 'acres' },
            { label: 'Hectares', value: 'hectares' },
            { label: 'Square Meters', value: 'sqm' },
            { label: 'Square Feet', value: 'sqft' },
          ],
          disabled: !isLand,
          required: isLand,
        }
      );
    }
    return fields;
  };
  const columns = useMemo(() => {
    const base = [
      { label: 'Title', accessor: 'title' },
      { label: 'Location', accessor: 'location' },
      { label: 'Google Maps', accessor: 'google_location', render: (value, record) => {
        const category = findCategory(record.category);
        const isMapCategory = category
          ? ['house', 'houses', 'home', 'homes', 'land', 'plot', 'plots'].includes(normalizeCategory(category.name))
          : false;
        return isMapCategory && value
          ? <a href={value} target="_blank" rel="noreferrer" className="text-blue-600 underline">Link</a>
          : 'N/A';
      }},
      {
        label: 'Details',
        accessor: 'bedrooms',
        render: (value, record) => {
          const category = findCategory(record.category);
          if (supportsLandFields && category && normalizeCategory(category.name) === 'land') {
            return record.land_size ? `${record.land_size} ${record.land_size_unit || 'acres'}` : 'N/A';
          }
          return value ? `${value} BR` : 'N/A';
        },
      },
      { label: 'Price', accessor: 'price', render: (value) => (value || value === 0 ? formatUGX(value) : 'Contact for Price') },
      { label: 'Status', accessor: 'status' },
      { label: 'Category', accessor: 'category', render: (value) => {
        const category = findCategory(value);
        return category ? category.name : value || 'Unknown';
      }},
    ];
    if (supportsLandFields) {
      base.splice(3, 0, {
        label: 'Land Size',
        accessor: 'land_size',
        render: (value, record) => {
          const category = findCategory(record.category);
          return (category && normalizeCategory(category.name) === 'land' && value)
            ? `${value} ${record.land_size_unit || ''}`.trim()
            : 'N/A';
        },
      });
    }
    return base;
  }, [categories, supportsLandFields]);

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleBulkUpload}
        accept=".csv"
        style={{ display: 'none' }}
      />
      <ResourceManager
        table="properties"
        title="Properties"
        description="Manage property inventory synced to the marketing site."
        columns={columns}
        formFields={getFormFields}
        searchable={true}
        searchPlaceholder="Search by title, location, or category..."
        searchFields={['title', 'location', 'category', 'status']}
        paginated={true}
        itemsPerPage={15}
        defaultValues={{
          title: '',
          location: '',
          google_location: '',
          bedrooms: '',
          ...(supportsLandFields ? { land_size: '', land_size_unit: 'acres' } : {}),
          price: '',
          status: 'available',
          category: categories.length > 0 ? categories[0].name : '',
          images: [],
          video_url: '',
          selected_agent_id: '',
          ...(supportsShortDescription ? { short_description: '' } : {}),
          description: '',
          is_featured: false,
        }}
        transformBeforeSave={(payload) => {
           const images = Array.isArray(payload.images) ? payload.images : [];
           const featuredImage = images.length > 0 ? images[0] : payload.featured_image || '';
           const selectedCategory = findCategory(payload.category);
           const isLand = selectedCategory && normalizeCategory(selectedCategory.name) === 'land';
           const isMapCategory = selectedCategory
             ? ['house', 'houses', 'home', 'homes', 'land', 'plot', 'plots'].includes(normalizeCategory(selectedCategory.name))
             : false;

           const result = {
             ...payload,
             google_location: isMapCategory ? (payload.google_location || '') : null,
             category: normalizeCategory(payload.category), // Ensure category is lowercase to match database constraint
             price: Number(payload.price) || 0,
             bedrooms: isLand ? null : (payload.bedrooms ? Number(payload.bedrooms) : null),
             featured_image: featuredImage,
             images: images,
             // Postgres UUID columns cannot accept empty strings, so coerce blanks to null
             selected_agent_id: payload.selected_agent_id ? payload.selected_agent_id : null,
           };
           
           // selected_agent_id is now a valid database field, keep it
           // This allows properties to have specifically assigned agents
           
           if (supportsLandFields) {
             result.land_size = isLand ? (payload.land_size ? parseLandSize(payload.land_size) : null) : null;
             result.land_size_unit = isLand ? payload.land_size_unit : null;
           } else {
             delete result.land_size;
             delete result.land_size_unit;
           }
           if (!supportsShortDescription) {
             delete result.short_description;
           }
           return result;
         }}
        onEdit={handleEdit}
        extraButtons={[
          { 
            label: 'Export CSV', 
            onClick: exportToCSV, 
            icon: Download,
            className: "inline-flex items-center rounded-lg border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 hover:border-stone-400 hover:shadow-md transition-all duration-200"
          },
          { 
            label: uploading ? 'Uploading...' : 'Bulk Upload', 
            onClick: () => fileInputRef.current.click(), 
            disabled: uploading, 
            icon: Upload,
            primary: true,
            className: "inline-flex items-center rounded-lg bg-lime-500 px-4 py-2 text-sm font-semibold text-stone-900 shadow-lg hover:bg-lime-400 hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          },
        ]}
      />
    </>
  );
};

export default PropertiesModule;
